/**
 * Unfreeze Account Modal Component
 * Matches the Claude artifact HTML structure
 */

import React, { useState } from 'react';
import './UnfreezeAccountModal.css';

interface UnfreezeAccountModalProps {
  userId: string;
  userName: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, notes: string) => Promise<void>;
}

const UNFREEZE_REASONS = [
  'Investigation Complete - No Issues',
  'Documents Verified',
  'Chargeback Resolved',
  'False Positive',
  'Manager Override',
];

const UnfreezeAccountModal: React.FC<UnfreezeAccountModalProps> = ({
  userId,
  userName,
  isOpen,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason) return;

    setLoading(true);
    try {
      await onConfirm(reason, notes);
      // Reset form
      setReason('');
      setNotes('');
      onClose();
    } catch (error) {
      console.error('Failed to unfreeze account:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <span className="modal-title">🔓 Unfreeze Account</span>
          <button className="detail-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body">
          <div className="alert info" style={{ marginBottom: '18px' }}>
            <div className="alert-icon">ℹ️</div>
            <div>
              <div className="alert-title">Unfreeze {userName}</div>
              <div className="alert-text">This will restore full account access.</div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Resolution *</label>
              <select
                className="form-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
              >
                <option value="">Select reason...</option>
                {UNFREEZE_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Resolution Notes (Audit Log)</label>
              <textarea
                className="form-input form-textarea"
                placeholder="Explain why account is being unfrozen..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
              />
            </div>

            <div className="modal-foot">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-success" disabled={loading || !reason}>
                {loading ? 'Unfreezing...' : '🔓 Unfreeze Account'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UnfreezeAccountModal;

